import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { useScrollToTop } from '../../hooks/useScrollToTop';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

type FaqItem = { q: string; a: string };

const FAQS: FaqItem[] = [
  {
    q: 'What is Founder Lab?',
    a: "Founder Lab is a virtual incubation platform for Pakistan's next generation of founders — a structured 6-phase program combining online coursework, group mentorship, pitch preparation, and investor access, all delivered online.",
  },
  {
    q: 'Who can join?',
    a: 'Early-stage founders — solo or in teams of up to 4. Universities, researchers, and investors can join under separate plans (Partner, Associate, Investor) with tailored read-only access to what our founders are building.',
  },
  {
    q: 'How long is the program?',
    a: 'The structured journey runs approximately 6 months across 6 sequential phases. You only move to the next phase after completing the current one — progress is gated to ensure foundational skills are solid before advancing.',
  },
  {
    q: 'Is it 1-on-1 mentorship?',
    a: 'No. Mentorship happens in small group sessions, not one-on-one. This keeps the experience collaborative and cost-effective, and founders benefit from peer learning alongside expert guidance.',
  },
  {
    q: 'Do I get a certificate?',
    a: 'Yes. Each phase you complete earns a phase certificate, and finishing the full 6-phase journey grants an incubation completion credential you can share on LinkedIn and with investors.',
  },
  {
    q: 'How do I become a mentor?',
    a: 'Apply via the "Become a Mentor" page. Upload your resume, share your areas of expertise, and tell us why you want to mentor. We review applications and reach out within 5 business days.',
  },
  {
    q: 'Can I get my money back?',
    a: "Plans are one-time purchases. We offer a 14-day refund window if you haven't yet started Phase 2 of the journey.",
  },
  {
    q: 'When can I pitch to investors?',
    a: 'Phase 6 — the final phase. You unlock investor visibility only after completing all coursework, attending mentor sessions, and getting a mentor-approved pitch. This keeps the investor surface high-signal.',
  },
];

const ChevronDown: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    className={`w-5 h-5 text-muted transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const BackArrow = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const FAQs: React.FC = () => {
  useScrollToTop();
  const listAnim = useScrollAnimation();
  const [open, setOpen] = useState<number | null>(0);

  return (
    <div className="min-h-screen w-full max-w-full overflow-x-hidden bg-bg text-text">
      <Header />

      <div className="section-gap pt-24 pb-12">
        {/* Back button */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-muted hover:text-text transition-colors"
        >
          <BackArrow />
          <span className="font-serif-display italic text-lg">Back</span>
        </Link>

        {/* Hero */}
        <div className="text-center py-8 md:py-12 animate-fade-in">
          <h1 className="font-display text-[3rem] md:text-[5rem] leading-none text-text uppercase tracking-wide mb-6">
            Frequently Asked Questions
          </h1>
          <p className="font-serif-display text-xl md:text-3xl italic text-muted max-w-3xl mx-auto">
            Quick answers to the most common questions about Founder Lab.
          </p>
        </div>

        {/* Accordion */}
        <div
          ref={listAnim.ref}
          className={`animate-on-scroll ${listAnim.isVisible ? 'visible' : ''}`}
        >
          <div className="space-y-4 max-w-4xl mx-auto">
            {FAQS.map((faq, i) => {
              const isOpen = open === i;
              return (
                <div
                  key={i}
                  className="bg-surface border border-line rounded-card overflow-hidden transition-colors hover:border-chart/40"
                >
                  <button
                    type="button"
                    onClick={() => setOpen(isOpen ? null : i)}
                    aria-expanded={isOpen}
                    className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none focus:ring-2 focus:ring-chart/30 focus:ring-inset"
                  >
                    <span className="font-display text-lg md:text-xl tracking-wide text-text pr-6">
                      {faq.q}
                    </span>
                    <ChevronDown open={isOpen} />
                  </button>
                  {isOpen && (
                    <div className="px-6 pb-6 pt-0 font-serif-display text-base md:text-lg italic text-muted leading-relaxed animate-fade-in">
                      {faq.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Still have questions CTA */}
        <div className="bg-surface border border-line rounded-card p-8 text-center max-w-3xl mx-auto">
          <h2 className="font-display text-2xl md:text-3xl tracking-wide text-text mb-3">
            Still have questions?
          </h2>
          <p className="font-serif-display text-lg italic text-muted mb-6">
            Drop us a line and we'll get back within one business day.
          </p>
          <a
            href="https://mail.google.com/mail/?view=cm&fs=1&to=ahmedbinaasim@gmail.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-chart text-white px-6 py-3 rounded-lg font-medium hover:bg-chart/90 transition-all duration-200 shadow-lg"
          >
            Contact Us
          </a>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default FAQs;
